import React, { useState, useEffect, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, FormikHelpers } from 'formik';

import {
  checkJobApplicationDuplicates,
  createJobApplication,
  DuplicateEmployee,
  DuplicateJobApplication,
  getLanguagesMinimalList,
  NewJobApplication,
} from './jobApplicationApi';
import { getResidencesMinimalList } from '../Residence/residenceApi';
import { validationSchema, initialValues } from './jobApplicationSchema';
import { commonStyles, css } from '../Common/styling';
import { useNotify } from '../Common/snackbarHooks';
import { CancelLink } from '../Common/ButtonLinks';
import JobApplicationForm from './components/JobApplicationForm';
import DuplicateJobApplicationsDialog from './components/DuplicateJobApplicationsDialog';
import { LanguageInterface } from './types';
import { HandleError } from '../Common/ErrorHandling/ErrorHelper';
import { OptionsInterface } from '../Common/types';
import { getResidenceGroupsMinimalList } from '../ResidenceGroup/residenceGroupApi';

type Props = {
  type: string;
};

const CreateJobApplication: React.FC<Props> = (props) => {
  // hooks
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useNotify();
  const [residenceMinimalList, setResidenceMinimalList] = useState<OptionsInterface[]>([]);
  const [residenceGroupsMinimalList, setResidenceGroupsMinimalList] = useState([]);
  const [languageMinimalList, setLanguageMinimalList] = useState<Array<LanguageInterface>>([]);
  // The employees and applications of the same person, found before creating the application
  const [duplicates, setDuplicates] = useState<{
    jobApplication: NewJobApplication;
    actions: FormikHelpers<any>;
    employees: DuplicateEmployee[];
    jobApplications: DuplicateJobApplication[];
  }>();
  const [selectedDuplicateId, setSelectedDuplicateId] = useState<number>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLanguagesMinimalList();
    getResidenceGroupsMinimalList({ is_active: true })
      .then(({ data }) => setResidenceGroupsMinimalList(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över avtalspart'));
    getResidencesMinimalList({ is_active: true })
      .then(({ data }) => setResidenceMinimalList(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över boenden'));
  }, []); // eslint-disable-line

  // functions
  // Checks for employees and applications of the same person first: an employee blocks the application, and
  // existing applications let the staff member choose to update one of them instead
  const handleFormSubmit = (
    { graduate_year, birth_date, languages, team, manually_tagged_residences, ...values }: typeof initialValues,
    actions: FormikHelpers<any>
  ): void => {
    const jobApplication: NewJobApplication = {
      ...values,
      type: props.type === 'participant' ? 'ParticipantJobApplication' : 'TeamLeaderJobApplication',
      birth_date: birth_date ?? undefined,
      graduate_year: graduate_year ?? undefined,
      language_ids: languages.map((language) => language.id as number),
      team_id: team?.id as number | undefined,
      manually_tagged_residence_ids: manually_tagged_residences.map((residence) => residence.id as number),
    };

    checkJobApplicationDuplicates(jobApplication)
      .then(({ data }) => {
        const { employees, job_applications: jobApplications } = data.data;
        if (employees.length === 0 && jobApplications.length === 0) {
          saveJobApplication(jobApplication, actions);
          return;
        }

        // The oldest application of the same person keeps the applicant's place in the queue, otherwise the oldest one
        setSelectedDuplicateId((jobApplications.find((ja) => ja.same_person) ?? jobApplications[0])?.id);
        setDuplicates({ jobApplication, actions, employees, jobApplications });
      })
      .catch((error: any) => {
        notifyError(`Det gick inte att kontrollera befintliga ansökningar: \n${HandleError(error.response?.data)}`);
        actions.setSubmitting(false);
      });
  };

  // Creates the application, or updates the existing one with the given id
  const saveJobApplication = (
    jobApplication: NewJobApplication,
    actions: FormikHelpers<any>,
    mergeIntoId?: number
  ): void => {
    setSaving(true);
    createJobApplication({ ...jobApplication, merge_into_id: mergeIntoId })
      .then(() => notifySuccess(mergeIntoId ? 'Ansökan har uppdaterats' : 'Ansökan har skapats'))
      .then(() => navigate(`/job_applications/${props.type}`))
      .catch((error: any) => {
        notifyError(`Det gick inte att spara ansökan: \n${HandleError(error.response?.data)}`);
        actions.setSubmitting(false);
      })
      .finally(() => {
        setSaving(false);
        setDuplicates(undefined);
      });
  };

  const cancelDuplicates = (): void => {
    duplicates?.actions.setSubmitting(false);
    setDuplicates(undefined);
  };

  const fetchLanguagesMinimalList = (): void => {
    getLanguagesMinimalList()
      .then(({ data }) => {
        setLanguageMinimalList(data.data);
      })
      .catch(() => notifyError('Det gick inte att hämta listan över språk'));
  };

  // render
  return (
    <React.Fragment>
      <div className={css(commonStyles.createViewHeader)}>
        <h1 className={css(commonStyles.headerTextStyle)}>
          {props.type === 'participant' ? 'Lägg till deltagaransökan' : 'Lägg till teamledareansökan'}
        </h1>
        <CancelLink link={`/job_applications/${props.type}`} />
      </div>

      <div className={css(commonStyles.formContainer)}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, actions): void => {
            handleFormSubmit(values, actions);
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            isValid,
            dirty,
          }): ReactElement => (
            <form onSubmit={handleSubmit}>
              <JobApplicationForm
                values={values}
                handleChange={handleChange}
                errors={errors}
                residenceMinimalList={residenceMinimalList}
                residenceGroupsMinimalList={residenceGroupsMinimalList}
                languageMinimalList={languageMinimalList}
                type={props.type}
                isCreate={true}
                setFieldValue={setFieldValue}
                submitDisabled={isSubmitting || !isValid || !dirty}
              />
            </form>
          )}
        </Formik>
      </div>

      {duplicates && (
        <DuplicateJobApplicationsDialog
          employees={duplicates.employees}
          jobApplications={duplicates.jobApplications}
          selectedId={selectedDuplicateId}
          onSelect={setSelectedDuplicateId}
          onCancel={cancelDuplicates}
          onUpdate={(): void => saveJobApplication(duplicates.jobApplication, duplicates.actions, selectedDuplicateId)}
          onCreate={(): void => saveJobApplication(duplicates.jobApplication, duplicates.actions)}
          submitting={saving}
        />
      )}
    </React.Fragment>
  );
};

export default CreateJobApplication;
